import Utils from "../../../../../../../../core/services/utils/utils";
import {PreventionInvalidityCode} from "../preventative-interactions";
import {BaseImportTransformer, toPartNumberUpper} from "./shared";

/**
 * Part notes import — part id resolution, visibility boolean, title/message payload.
 * Follows the multi-phase contract on {@link BaseImportTransformer}; phase-4 checks run for every
 * row with an item so INVALID_BOOLEAN / MISSING_REQUIRED_FIELDS can combine with INVALID_PART.
 * Preview (`!isFinalRun`): placeholder rows where needed + merged `preventativeIssues`.
 * Final (`isFinalRun`): `apiRequests[i]` is `null` when the row has a preventative issue.
 */
export class PartNotesImportTransformer extends BaseImportTransformer {
    /**
     * @param {Array<Object|null|undefined>} mappedData
     * @param {boolean} isFinalRun
     */
    async run(mappedData, isFinalRun = false) {
        const rows = mappedData ?? [];
        const preventativeIssues = [];

        /** @type {{index: number, item: *, partNoUpper: string, codes: string[]}[]} */
        const rowState = rows.map((item, index) => ({
            index,
            item,
            partNoUpper: toPartNumberUpper(item?.partNumber),
            codes: [],
        }));

        // ── Phase 1: preliminary (CSV-only) ─────────────────────────────────────
        for (const rs of rowState) {
            if (!rs.item) continue;
            if (!rs.partNoUpper) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
            }
        }

        // ── Phase 2: batched part lookup ────────────────────────────────────────
        const uniqueParts = [...new Set(rowState.map((r) => r.partNoUpper).filter(Boolean))].sort();
        /** @type {Record<string, *>} */
        let partIds = {};
        if (uniqueParts.length > 0) {
            const bundle = await this.ctx.fetchPartInfoByPartNumbers(uniqueParts);
            partIds = bundle.partIds;
        }

        // ── Phase 3: unknown part ───────────────────────────────────────────────
        for (const rs of rowState) {
            if (!rs.item) continue;
            if (rs.partNoUpper && !partIds[rs.partNoUpper]) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
            }
        }

        // ── Phase 4: field validation (boolean + required title/message regardless of part resolution) ──
        for (const rs of rowState) {
            if (!rs.item) continue;

            const isPrivate = Utils.tryConvertStringToBoolean(rs.item.isPrivate);
            if (isPrivate === undefined) {
                rs.codes.push(PreventionInvalidityCode.INVALID_BOOLEAN);
            }
            const title = rs.item.title ?? "";
            const msg = rs.item.message ?? "";
            if (!title || !msg) {
                rs.codes.push(PreventionInvalidityCode.MISSING_REQUIRED_FIELDS);
            }
        }

        // ── Emit merged issues ───────────────────────────────────────────────────
        for (const rs of rowState) {
            if (!rs.item) {
                preventativeIssues.push({
                    rowIndex: rs.index,
                    originalRow: rows[rs.index],
                    invalidityCodes: [PreventionInvalidityCode.INVALID_PART],
                });
                continue;
            }
            if (!rs.codes.length) continue;
            preventativeIssues.push({
                rowIndex: rs.index,
                originalRow: rs.item,
                invalidityCodes: [...new Set(rs.codes)],
            });
        }

        const issueIndexSet = new Set(preventativeIssues.map((p) => p.rowIndex));

        const apiRequests = rows.map((item, index) => {
            if (!item) {
                return isFinalRun ? null : {partId: null, msg: undefined};
            }

            const rs = rowState[index];
            if (!rs.partNoUpper) {
                return isFinalRun ? null : {partId: null, msg: undefined};
            }

            const partId = partIds[rs.partNoUpper];
            const hasIssue = issueIndexSet.has(index);
            const isPrivate = Utils.tryConvertStringToBoolean(item.isPrivate);

            if (hasIssue) {
                return isFinalRun
                    ? null
                    : {
                        partId: partId ?? null,
                        title: item.title,
                        msg: item.message,
                        isForClient: isPrivate === undefined ? isPrivate : !isPrivate,
                    };
            }

            return {
                partId,
                title: item.title ?? undefined,
                msg: item.message ?? undefined,
                isForClient: isPrivate === undefined ? isPrivate : !isPrivate,
            };
        });

        return {apiRequests, preventativeIssues};
    }
}
